import React, { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { getDirtyValues, isFormDirty, withClearedFields } from "@/lib/utils";
import { parseRead } from "@/lib/zod/read-schema";
import { ArtistRefField } from "@/pages/people/components/fields/artist-ref-field";
import { usePeopleRepository } from "@/pages/people/hooks/use-people-repository";
import { toTranslationRows } from "@/pages/people/schemas/person-translations";
import {
  PersonEditFormType,
  personEditSchema,
} from "@/pages/people/schemas/person.edit.form";

const schemaProvider = new ZodProvider(personEditSchema);

/**
 * Text fields a curator may empty on purpose. Each was checked against `UpdatePersonCommand`:
 * `""` binds and is stored, and for `artistId` both BFFs read `""` as "no linked artist".
 * `slug` is not here — an empty slug is rejected — and `name` is required.
 */
const CLEARABLE_FIELDS = ["about", "knownForDepartment", "artistId"];

/**
 * A person document can carry `media: null` (a TMDB person with no profile photo), but the form's
 * media field needs the two lists. Seeding them empty changes nothing on save — `media` is only
 * sent once the curator adds a photo, and then it is sent whole.
 */
function toPersonForm(raw: unknown): unknown {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  const record = raw as Record<string, any>;
  return {
    ...record,
    media: {
      photos: record.media?.photos ?? [],
      teasers: record.media?.teasers ?? [],
    },
    translations: toTranslationRows(record.translations),
  };
}

/**
 * Edit one person — cast and crew of movies, plays and performances. The backend writes a new
 * name or first photo through to every cast list that carries the person.
 *
 * No delete button, unlike `MoviesEdit`: `DeletePersonCommand` removes the document and nothing
 * else, so every cast credit linked to the person would keep a dangling `personId`.
 */
export const PeopleEdit = () => {
  const { get, update } = usePeopleRepository();
  const { id } = useParsed();
  const [values, setValues] = React.useState<PersonEditFormType>();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();
  const setTitle = useDocumentTitle();

  useEffect(() => {
    if (!id) {
      open?.({
        type: "error",
        message: "Something went wrong. This shouldn't be happening",
      });
      return;
    }
    setLoading(true);
    get(id.toString())
      .then((response) => {
        const data = parseRead(
          personEditSchema,
          toPersonForm(response),
          "person",
        ) as PersonEditFormType;
        setValues(data);
        setTitle("Edit " + (data.name ?? "person"));
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get person",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const initializeForm = (
    form: UseFormReturn<PersonEditFormType, any, PersonEditFormType>,
  ) => {
    form.watch((value) => {
      setWarnWhen(
        form.formState.isDirty &&
          isFormDirty(
            form.formState.dirtyFields as Record<string, unknown>,
            value as Record<string, unknown>,
            form.formState.defaultValues as Record<string, unknown>,
          ),
      );
    });
  };

  const onSubmit = async (
    allValues: PersonEditFormType,
    form: UseFormReturn<PersonEditFormType, any, PersonEditFormType>,
  ) => {
    const changed = getDirtyValues(
      form.formState.dirtyFields as Record<string, unknown>,
      allValues,
    );
    // AutoForm strips emptied fields before we get here — re-add the ones the curator cleared
    // on purpose as "".
    const dirty = withClearedFields<PersonEditFormType>(
      changed,
      form.getValues() as Record<string, unknown>,
      form.formState.defaultValues as Record<string, unknown>,
      CLEARABLE_FIELDS,
    );
    // The backend replaces the whole map, and AutoForm drops an emptied list: send every row
    // the form holds, read raw, so a removed locale is removed.
    if ("translations" in changed || "translations" in dirty) {
      const raw = form.getValues() as Record<string, unknown>;
      dirty.translations = (raw.translations ??
        []) as PersonEditFormType["translations"];
    }
    setWarnWhen(false);
    await update({ id: allValues.id, ...dirty });
  };

  return (
    <div className="flex flex-row">
      <div className="flex w-full flex-col space-y-4 p-8">
        {values && (
          <AutoForm
            schema={schemaProvider}
            defaultValues={values}
            onSubmit={onSubmit}
            onFormInit={initializeForm}
            {...injectCustomAutoFormComponents({
              formComponents: { artistRef: ArtistRefField },
            })}
          >
            <BottomActionBar>
              <Button disabled={!warnWhen} type="submit">
                Submit
              </Button>
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
    </div>
  );
};
